// components/crm/ClientRoleManagement.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Table,
  Button,
  Card,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  Typography,
  Spin
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ClientRole } from '../../api/CRMApiClient';
import { useStore } from '../../stores/root-store-context';

const ClientRoleManagement: React.FC = observer(() => {
  const { t } = useTranslation();
  const { crmStore } = useStore();

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<ClientRole | null>(null);
  const [form] = Form.useForm();

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Filters                                                                 */
  /* ───────────────────────────────────────────────────────────────────────── */
  const [search, setSearch] = useState<string>('');

  const filteredRoles = useMemo(() => {
    const q = search.toLowerCase();
    return q ? crmStore.clientRoles.filter((r) => r.name.toLowerCase().includes(q)) : crmStore.clientRoles;
  }, [crmStore.clientRoles, search]);

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Data loading                                                            */
  /* ───────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    crmStore.getClientRoles();
  }, [crmStore]);

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  CRUD helpers                                                            */
  /* ───────────────────────────────────────────────────────────────────────── */
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setModal(true);
  };

  const openEdit = (role: ClientRole) => {
    setEditing(role);
    form.setFieldsValue(role);
    setModal(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    if (editing) {
      await crmStore.updateClientRole(editing.id, values);
    } else {
      await crmStore.addClientRole(values);
    }
    setModal(false);
  };

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Table columns                                                           */
  /* ───────────────────────────────────────────────────────────────────────── */
  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: '10%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '70%' },
    {
      title: t('Actions'),
      key: 'actions',
      width: '20%',
      render: (_: unknown, record: ClientRole) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title={t('DeleteRoleConfirm')} onConfirm={() => crmStore.deleteClientRole(record.id)}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  /* ───────────────────────────────────────────────────────────────────────── */
  /*  Render                                                                  */
  /* ───────────────────────────────────────────────────────────────────────── */
  return (
    <Spin spinning={crmStore.isLoading}>
      <Card
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            {t('AddRole')}
          </Button>
        }
      >
        {/* Filter */}
        <Input.Search
          placeholder={t('Search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 240, marginBottom: 16 }}
        />

        <Table dataSource={filteredRoles} columns={columns} rowKey="id" pagination={false} />

        {/* Modal */}
        <Modal
          title={editing ? t('EditRole') : t('AddRole')}
          open={modal}
          onOk={save}
          onCancel={() => setModal(false)}
          destroyOnClose
          okText={t('OK')}
          cancelText={t('Cancel')}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="name"
              label={t('Name')}
              rules={[{ required: true, message: t('PleaseEnterName') }]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </Spin>
  );
});

export default ClientRoleManagement;
